import cn from 'classnames';
import React from 'react';

import type { SelectorListItem } from '../../types/store/selector-list-item';
import type { IPriceForStoreItem } from '../../types/store/store-item-price';
import type {
  StoreImageSizes,
  StoreSizeTemplate,
} from '@site-builder/common/src/types/blocks';
import type { FiveSymbolLocale } from '@site-builder/common/src/types/locale';
import type { LocalItemType } from '@site-builder/common/src/types/store';

import StoreButtons from '../../components/Blocks/Store/Buttons/StoreButtons';
import StorePrice from '../../components/Blocks/Store/StorePrice/StorePrice';
import BundleModal from '../../components/ModalWindows/BundleModal/BundleModal';
import { getShowMoreText } from '../../utils/translation';
import { UniversalCart } from '../blocks/store/cart/UniversalCart';
import { enableLongDescription } from '../landingAPI/enableLongDescription';

export interface IStoreProps {
  isEmptyList: boolean;
  emptyListText: string;
  id?: string;
  itemGameClass?: string;
  itemBundleClass?: string;
  image?: string;
  name: string;
  description?: string;
  longDescription?: string;
  price: IPriceForStoreItem;
  isPreOrder?: boolean;
  buttonId: string;
  itemType: string;
  itemSku: string;
  buttonText: string;
  requireAuth?: boolean;
  xaExtra?: string;
  enableCart?: boolean;
  selectorList?: SelectorListItem[];
  cartSku?: string;
  purchasedText?: string;
  descriptionEnable?: boolean;
  xaElementValue?: LocalItemType;
  isTextNeeded?: boolean;
  cart: UniversalCart;
  content: any[];
  closeBundleInfoModal: (sku: string) => void;
  locale: FiveSymbolLocale;
  imageSize: StoreImageSizes;
  showMoreEnable: boolean;
  addToCart?: (sku?: string) => void;
  cardSize: StoreSizeTemplate;
  showPriceInButton?: boolean;
  handlerSelect: (value: string | number) => void;
  selectButton?: boolean;
}

const Store: React.FC<IStoreProps> = ({
  isEmptyList,
  emptyListText,
  id = '',
  itemGameClass = '',
  itemBundleClass = '',
  image = '',
  name,
  description,
  longDescription,
  isPreOrder,
  buttonId,
  itemType,
  itemSku,
  buttonText,
  requireAuth,
  xaExtra,
  enableCart,
  selectorList = [],
  cartSku,
  purchasedText,
  descriptionEnable,
  xaElementValue,
  isTextNeeded,
  cart,
  content,
  price,
  closeBundleInfoModal,
  locale,
  imageSize,
  showMoreEnable,
  addToCart,
  cardSize,
  showPriceInButton,
  handlerSelect,
  selectButton,
}: IStoreProps) =>
  isEmptyList ? (
    <div className='store__empty-section'>{emptyListText}</div>
  ) : (
    <li
      className='store__item-wrapper'
      id={`store-item-${id}`}
      data-buy-button-item=''
    >
      <div
        className={`store__item ${itemGameClass} ${itemBundleClass} ${
          price.hasDiscount ? 'store__item--with-discount' : ''
        }`}
        data-clickable={showMoreEnable}
        data-sku={cartSku}
      >
        {price.hasDiscount && price.discountAmount && (
          <div className='store__item-ribbon'>{`${price.discountAmount}%`}</div>
        )}
        <div
          className={cn(
            'store__item-img lazy',
            imageSize && `store__item-img--${imageSize}`,
            itemGameClass,
            itemBundleClass
          )}
          data-bg={`url(${image})`}
          style={{ backgroundSize: imageSize }}
        />
        <div className='store__item-body'>
          <h5 className='store__item-title'>{name}</h5>
          {descriptionEnable && (
            <span
              className={`store__item-description ${itemGameClass} ${itemBundleClass}`}
            >
              {description}
            </span>
          )}
          {enableLongDescription() && (
            <span className='store__item-long-description'>
              {longDescription}
            </span>
          )}
        </div>
        <div className='store__item-footer'>
          {!showPriceInButton && (
            <StorePrice price={price} className={`store__price--${cardSize}`} />
          )}
          <StoreButtons
            buttonId={buttonId}
            buttonText={
              showPriceInButton ? (
                <StorePrice price={price} className='store__price--button' />
              ) : (
                buttonText
              )
            }
            enableCart={enableCart && !showMoreEnable}
            isForVc={price.isForVc}
            isPreOrder={isPreOrder}
            requireAuth={requireAuth}
            type={itemType}
            sku={itemSku}
            locale={locale}
            isTextNeeded={isTextNeeded}
            selectorList={selectorList}
            cart={cart}
            vcSku={price.isForVc ? price.vcPrice?.sku : undefined}
            xaElementValue={xaElementValue}
            xaExtra={xaExtra}
            addToCart={addToCart}
            closeBundleInfoModal={closeBundleInfoModal}
            handlerSelect={handlerSelect}
            selectButton={selectButton}
          />
          <span data-sku={itemSku} data-type='purchased-text' hidden>
            {purchasedText}
          </span>
        </div>
        {showMoreEnable && (
          <>
            <button
              className='store__show-more-btn'
              data-button-type='show-more'
              data-sku={cartSku}
            >
              {getShowMoreText(locale)}
            </button>
            <BundleModal
              sku={itemSku}
              onClose={() => closeBundleInfoModal(itemSku)}
              content={content}
              price={price}
              isServerSideRender
              name={name}
              description={description}
              img={image}
              imageSize={imageSize}
              locale={locale}
            >
              <StoreButtons
                className='bundle-modal__buttons'
                buttonsClassPrefix='bundle-modal'
                buttonId={`${buttonId}--modal`}
                buttonText={buttonText}
                enableCart={enableCart}
                isForVc={price.isForVc}
                isPreOrder={isPreOrder}
                requireAuth={requireAuth}
                type={itemType}
                sku={itemSku}
                locale={locale}
                isTextNeeded={isTextNeeded}
                selectorList={selectorList}
                cart={cart}
                vcSku={price.isForVc ? price.vcPrice?.sku : undefined}
                xaElementValue={xaElementValue}
                xaExtra={xaExtra}
                addToCart={addToCart}
                handlerSelect={handlerSelect}
                section='pop-up'
                closeBundleInfoModal={closeBundleInfoModal}
              />
            </BundleModal>
          </>
        )}
      </div>
    </li>
  );

export default Store;
